import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, DefaultValueAccessor, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { IconComponent } from '@libs/components/icons';
import { AutocompleteInputModel, AutocompleteOptionModel } from '@libs/form/models';
import { map, Observable } from 'rxjs';
import { ErrorDirective, InputAttributesDirective, InputValidatorsDirective } from '../../../directives';
import { InputBaseComponent } from '../../../models';
import { FormInputDescriptionComponent } from '../../description';
import { InputProviderFactory } from '../ng-input-provider';

@Component({
  selector: 'ui-form-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [
    ...InputProviderFactory.getProviders(AutocompleteComponent),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    ErrorDirective,
    InputAttributesDirective,
    InputValidatorsDirective,
    AsyncPipe,
    MatButtonModule,
    IconComponent,
    FormInputDescriptionComponent,
  ],
})
export class AutocompleteComponent extends InputBaseComponent implements OnInit, AfterViewInit {
  @Input()
  public data!: AutocompleteInputModel;

  @ViewChild(DefaultValueAccessor, { static: true })
  public valueAccessor!: ControlValueAccessor;

  @ViewChild('input', { static: true, read: ElementRef })
  public inputRef!: ElementRef<HTMLInputElement>;

  @ViewChild('autocompleteTrigger', { static: true, read: MatAutocompleteTrigger })
  public autocompleteTrigger!: MatAutocompleteTrigger;

  public filteredOptions$!: Observable<AutocompleteOptionModel[]>;

  public override ngOnInit(): void {
    super.ngOnInit();

    this.filteredOptions$ = this.control.valueChanges.pipe(
      map(this.filterOptions.bind(this)),
    );
  }

  public ngAfterViewInit(): void {
    // In order to guarantee filtered options to be correct after initialization with [formControl]
    // we need to trigger valueChanges observer
    this.control.setValue(this.control.value);
  }

  /**
   * Gets option label by its value
   */
  public displayFn(value: string): string {
    const option: AutocompleteOptionModel | undefined = this.data.options.find(
      (item: AutocompleteOptionModel) => value === item.value,
    );

    return option?.label ?? value;
  }

  public clearInput(event: Event): void {
    event.stopPropagation();
    this.control.setValue('');
    this.autocompleteTrigger.openPanel();
    this.inputRef.nativeElement.focus();
  }

  public expandMore(event: Event): void {
    event.stopPropagation();
    this.autocompleteTrigger.openPanel();
    this.inputRef.nativeElement.focus();
  }

  /**
   * Filters autoselect options
   */
  private filterOptions(labelPartial: string): AutocompleteOptionModel[] {
    // Return full list of options if input is empty or valid option is selected
    if (!labelPartial || !this.valueIsUserInput()) {
      return this.data.options;
    }

    // Filter options
    const pattern: string = labelPartial.toLowerCase();

    return this.data.options.filter(
      (option: AutocompleteOptionModel) => option.label.toLowerCase().includes(pattern),
    );
  }

  /**
   * Determines whether current control contains user input text or the value of selected option
   */
  private valueIsUserInput(): boolean {
    const controlValue: string = String(this.control.value);
    const inputValue: string = this.inputRef.nativeElement.value;

    // If input and control values are not equal then both were written by mat-autocomplete
    if (inputValue !== controlValue) {
      return false;
    }

    // If input is equal to initial value and the control hasn't been touched
    // than it has not yet been initialized and overwritten by display function
    if (inputValue === this.data.value && this.control.pristine) {
      return false;
    }

    // All other cases considered as user input
    return true;
  }
}
